// ── Observer Mode Session (P12.2) ──
// SuperAdmin-only read-only observation of contractor/installer surfaces.
//
// Hard rules:
//   • sessionStorage ONLY — never localStorage. Auto-clears on tab close
//     and never clobbers a real subcontractor's seeded identity.
//   • Only SuperAdmin may activate. The hook gates by role; this module is
//     storage plumbing and is role-agnostic.
//   • Observation is read-only. No canonical mutations. No audit events.
//   • Telemetry from observer sessions is tagged so it can be excluded
//     from real contractor/installer metrics.

export type ObserverKind = "contractor" | "installer";

const KEYS: Record<ObserverKind, string> = {
  contractor: "koredor.observer.contractor",
  installer: "koredor.observer.installer",
};

function safeSession(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function setObserverIdentity(kind: ObserverKind, id: string): void {
  const s = safeSession();
  if (!s || !id) return;
  try {
    s.setItem(KEYS[kind], id);
  } catch {
    /* noop */
  }
}

export function getObserverIdentity(kind: ObserverKind): string | null {
  const s = safeSession();
  if (!s) return null;
  try {
    const v = s.getItem(KEYS[kind]);
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

export function clearObserverIdentity(kind: ObserverKind): void {
  const s = safeSession();
  if (!s) return;
  try {
    s.removeItem(KEYS[kind]);
  } catch {
    /* noop */
  }
}

export function clearAllObserverIdentities(): void {
  clearObserverIdentity("contractor");
  clearObserverIdentity("installer");
}

/** True if any observer identity is currently set in session. */
export function hasAnyObserverIdentity(): boolean {
  return (
    getObserverIdentity("contractor") != null ||
    getObserverIdentity("installer") != null
  );
}
